import "./Navbar.css";

import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { FaBars, FaShoppingBag, FaTimes, FaUserCircle } from "react-icons/fa";

import logo from "../../assets/images/logo.png";
import { useCart } from "../../context/CartContext";

const navigationLinks = [
  { to: "/", label: "בית" },
  { to: "/product", label: "המוצר" },
  { to: "/doctor", label: "אודות" },
  { to: "/reviews", label: "ביקורות" },
  { to: "/gallery", label: "גלריה" },
  { to: "/contact", label: "צור קשר" },
];

function Navbar() {
  const { cartItems, clearCart } = useCart();

  const [menuOpen, setMenuOpen] = useState(false);

  const headerRef = useRef(null);
  const toggleRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const cartCount = cartItems.reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0,
  );

  const links =
    token && role === "admin"
      ? [...navigationLinks, { to: "/admin", label: "ניהול" }]
      : navigationLinks;

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const navClass = ({ isActive }) =>
    isActive ? "nav-link active" : "nav-link";

  // إغلاق قائمة الهاتف عند الانتقال لصفحة أخرى
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  // إغلاق قائمة الهاتف عند الانتقال لحجم شاشة الكمبيوتر
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1161px)");

    const handleResize = () => {
      if (mediaQuery.matches) {
        setMenuOpen(false);
      }
    };

    mediaQuery.addEventListener("change", handleResize);

    return () => {
      mediaQuery.removeEventListener("change", handleResize);
    };
  }, []);

  // إغلاق القائمة عند الضغط خارجها أو الضغط على Escape
  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };

    const handleFocus = (event) => {
      if (!headerRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("focusin", handleFocus);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("focusin", handleFocus);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    clearCart();

    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("user");

    setMenuOpen(false);

    // انتقال داخلي بدون تحديث كامل للصفحة
    navigate("/login", { replace: true });
  };

  return (
    <header ref={headerRef} className="navbar-v2" dir="rtl" lang="he">
      <Link
        to="/"
        className="brand-v2"
        onClick={closeMenu}
        aria-label="Cervica — עמוד הבית"
      >
        <img src={logo} alt="Cervica" />
      </Link>

      <nav className="nav-links-v2" aria-label="ניווט ראשי">
        {links.map(({ to, label }) => (
          <NavLink key={to} to={to} end={to === "/"} className={navClass}>
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="nav-actions-v2">
        {token && (
          <Link
            to="/cart"
            className="icon-action-v2"
            onClick={closeMenu}
            aria-label={`סל קניות, ${cartCount} פריטים`}
          >
            <FaShoppingBag aria-hidden="true" />

            {cartCount > 0 && (
              <span className="cart-count-v2" aria-hidden="true">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </Link>
        )}

        {!token ? (
          <Link to="/login" className="nav-login-v2" onClick={closeMenu}>
            התחברות
          </Link>
        ) : (
          <div className="account-actions-v2">
            <Link
              to="/profile"
              className="profile-v2"
              onClick={closeMenu}
              aria-label="הפרופיל שלי"
            >
              <FaUserCircle aria-hidden="true" />
            </Link>

            <button type="button" onClick={handleLogout} className="logout-v2">
              התנתקות
            </button>
          </div>
        )}

        <button
          ref={toggleRef}
          type="button"
          className="mobile-toggle-v2"
          aria-label={menuOpen ? "סגירת תפריט" : "פתיחת תפריט"}
          aria-expanded={menuOpen}
          aria-controls="cervica-mobile-menu"
          onClick={() => setMenuOpen((current) => !current)}
        >
          {menuOpen ? (
            <FaTimes aria-hidden="true" />
          ) : (
            <FaBars aria-hidden="true" />
          )}
        </button>
      </div>

      <nav
        id="cervica-mobile-menu"
        className="mobile-menu-v2"
        aria-label="ניווט במכשיר נייד"
        hidden={!menuOpen}
      >
        <div className="mobile-menu-inner-v2">
          <span className="mobile-menu-kicker">ניווט באתר</span>

          {links.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={navClass}
              onClick={closeMenu}
            >
              {label}
            </NavLink>
          ))}

          <div className="mobile-account-v2">
            {token ? (
              <>
                <NavLink to="/profile" className={navClass} onClick={closeMenu}>
                  הפרופיל שלי
                </NavLink>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="mobile-logout-v2"
                >
                  התנתקות
                </button>
              </>
            ) : (
              <Link to="/login" className="mobile-login-v2" onClick={closeMenu}>
                התחברות לחשבון
              </Link>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
